/**
 * HotelbedsAdminPanel — Phase 1 + Phase 4 admin dashboard section.
 *
 * Phase 1 (2026-07-27): ENV badge (SANDBOX green / LIVE red) +
 * recent supplier=hotelbeds bookings list.
 *
 * Phase 4 (2026-07-27): Cert Readiness card — aggregates the
 * operational signals a Hotelbeds reviewer will inspect before
 * granting live-key access (env configured, booking exercised,
 * voucher generated, cancel flow tested, content cache populated).
 * LIVE badge now carries a lock icon so the operator immediately
 * recognises they are hitting the production API.
 */
import React, { useEffect, useState } from "react";
import {
  BedDouble, RefreshCw, Info, Lock, Shield, CheckCircle2, XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import PoweredByHotelbeds from "@/components/hotels/PoweredByHotelbeds";

export default function HotelbedsAdminPanel() {
  const [env, setEnv] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [includeAllEnvs, setIncludeAllEnvs] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const bookingsUrl = includeAllEnvs
      ? "/admin/bookings?supplier=hotelbeds&limit=25&include_all_envs=true"
      : "/admin/bookings?supplier=hotelbeds&limit=25";
    Promise.all([
      api.get("/admin/hotelbeds/env").catch(() => ({ data: null })),
      api.get("/admin/hotelbeds/cert-readiness").catch(() => ({ data: null })),
      api.get(bookingsUrl).catch(() => ({ data: { bookings: [] } })),
    ]).then(([e, r, b]) => {
      setEnv(e.data);
      setReadiness(r.data);
      setBookings(b.data.bookings || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [includeAllEnvs]);

  const isSandbox = env?.env === "SANDBOX";
  // Keep SANDBOX green (shipped in Phase 1 — do not confuse operators
  // by swapping to amber). LIVE stays red with a lock icon.
  const badgeCls = isSandbox
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : "bg-red-100 text-red-800 border-red-300";

  return (
    <section
      data-testid="admin-hotelbeds-panel"
      className="rounded-2xl border-2 border-tb-cream-3 bg-white p-5"
    >
      <header className="flex flex-wrap items-center gap-3">
        <BedDouble className="w-5 h-5 text-tb-navy" />
        <h3 className="font-serif text-tb-navy text-xl" style={{ fontFamily: '"Playfair Display", serif' }}>
          Hotelbeds bookings
        </h3>
        {env && (
          <span
            data-testid="admin-hotelbeds-env-badge"
            className={"inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-hand uppercase tracking-widest " + badgeCls}
          >
            {!isSandbox && <Lock className="w-3 h-3" data-testid="admin-hotelbeds-env-lock" />}
            ENV: {env.env}
          </span>
        )}
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full border-2 border-tb-cream-3 hover:border-tb-navy font-hand text-tb-navy/70 text-xs tb-tap disabled:opacity-40"
        >
          <RefreshCw className={"w-3 h-3 " + (loading ? "animate-spin" : "")} /> Refresh
        </button>
      </header>

      {env && (
        <p className="mt-2 font-hand text-tb-navy/60 text-xs italic flex items-center gap-1.5">
          <Info className="w-3 h-3" /> {env.base_url}
          {" · "}api-key: <span className="font-mono">{env.masked_api_key || "—"}</span>
          {" · "}configured: {String(env.configured)}
        </p>
      )}

      {/* Phase 4: Cert Readiness card */}
      {readiness && (
        <div
          data-testid="admin-hotelbeds-cert-readiness"
          className="mt-4 rounded-xl border border-tb-cream-3 bg-tb-cream-2/50 p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-tb-navy" />
            <span className="font-serif text-tb-navy text-base" style={{ fontFamily: '"Playfair Display", serif' }}>
              Cert readiness ({readiness.env})
            </span>
            <span
              data-testid="admin-hotelbeds-cert-status"
              className={
                "ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-hand uppercase tracking-widest border " +
                (readiness.ready
                  ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                  : "bg-amber-50 text-amber-800 border-amber-300")
              }
            >
              {readiness.ready ? "READY" : "IN PROGRESS"}
            </span>
          </div>
          <ul className="space-y-1.5">
            {(readiness.checks || []).map((c) => (
              <li
                key={c.key}
                data-testid={`admin-hotelbeds-cert-check-${c.key}`}
                className="flex items-start gap-2 font-hand text-sm text-tb-navy/85"
              >
                {c.ok
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  : <XCircle className="w-4 h-4 text-tb-navy/40 mt-0.5 flex-shrink-0" />}
                <span className="flex-1">
                  {c.label}
                  {c.detail && (
                    <span className="text-tb-navy/50 italic"> — {c.detail}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-hand text-tb-navy/70 text-xs uppercase tracking-widest">
            Recent bookings
          </span>
          <label className="inline-flex items-center gap-1.5 font-hand text-tb-navy/70 text-xs cursor-pointer">
            <input
              type="checkbox"
              data-testid="admin-hotelbeds-include-all-envs"
              checked={includeAllEnvs}
              onChange={(e) => setIncludeAllEnvs(e.target.checked)}
              className="w-3.5 h-3.5 accent-tb-navy cursor-pointer"
            />
            Include all envs
          </label>
        </div>
        {loading ? (
          <p className="font-hand text-tb-navy/55 py-4">Loading…</p>
        ) : bookings.length === 0 ? (
          <p data-testid="admin-hotelbeds-empty" className="font-hand text-tb-navy/55 py-4 italic">
            No Hotelbeds bookings yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-hand text-tb-navy/85">
              <thead className="text-tb-navy/50 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left pb-2 pr-3">Reference</th>
                  <th className="text-left pb-2 pr-3">Hotel</th>
                  <th className="text-left pb-2 pr-3">Dates</th>
                  <th className="text-right pb-2 pr-3">Total</th>
                  <th className="text-left pb-2 pr-3">Env</th>
                  <th className="text-left pb-2 pr-3">User</th>
                  <th className="text-left pb-2">Status</th>
                </tr>
              </thead>
              <tbody data-testid="admin-hotelbeds-bookings-tbody">
                {bookings.map((b) => (
                  <tr key={b.hotelbeds_reference} className="border-t border-tb-cream-3">
                    <td className="py-2 pr-3 font-mono text-xs">{b.hotelbeds_reference}</td>
                    <td className="py-2 pr-3">{b.hotel_name}</td>
                    <td className="py-2 pr-3">{b.check_in} → {b.check_out}</td>
                    <td className="py-2 pr-3 text-right">{b.total_price} {b.currency}</td>
                    <td className="py-2 pr-3">
                      <span
                        data-testid="admin-hotelbeds-row-env"
                        className={
                          "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-hand uppercase tracking-widest border " +
                          ((b.env || "sandbox") === "sandbox"
                            ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                            : "bg-red-50 text-red-800 border-red-300")
                        }
                      >
                        {b.env || "sandbox"}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-xs">{b.user_id ? b.user_id.slice(0, 8) + "…" : "—"}</td>
                    <td className="py-2 text-xs">{b.status || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <PoweredByHotelbeds size="xs" />
        <span className="font-hand text-tb-navy/40 text-[10px]">Supplier: hotelbeds</span>
      </div>
    </section>
  );
}
